import { Maybe, UserId, VisitId } from '@csp/csp-common-model';
import { SetURLSearchParams, createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BioSampleCode, BioSampleId, BioSampleKitCode, BioSampleRef } from '@csp/csp-common-hbs-model';
import { useCallback } from 'react';
import { HbsSearchParamKey } from '../type/HbsSearchParamKey';
import { CspPath } from '../../../CspPath';

type NavigateIn = {
  to: CspPath;
  samples: Pick<BioSampleRef, 'sampleCode'>[];
};

type Out = {
  visitId: Maybe<VisitId>;
  patientId: Maybe<UserId>;
  sampleId: Maybe<BioSampleId>;
  sampleCode: Maybe<BioSampleCode>;
  sampleCodes: BioSampleCode[];
  kitCode: Maybe<BioSampleKitCode>;
  parentSampleId: Maybe<BioSampleId>;
  setSearchParams: SetURLSearchParams;
  clearSearchParams(keys: string[]): void;
  navigateWithParams(options: NavigateIn): void;
  replaceSampleCodeWithId(sampleId: BioSampleId): void;
};

export const useHbsSearchParams = (): Out => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const visitId = searchParams.get(HbsSearchParamKey.VISIT_ID) ?? undefined;
  const patientId = searchParams.get(HbsSearchParamKey.PATIENT_ID) ?? undefined;
  const sampleId = searchParams.get(HbsSearchParamKey.SAMPLE_ID) ?? undefined;
  const sampleCode = searchParams.get(HbsSearchParamKey.SAMPLE_CODE) ?? undefined;
  const sampleCodes = searchParams.getAll(HbsSearchParamKey.SAMPLE_CODES) ?? [];
  const kitCode = searchParams.get(HbsSearchParamKey.KIT_CODE) ?? undefined;
  const parentSampleId = searchParams.get(HbsSearchParamKey.PARENT_SAMPLE_ID) ?? undefined;

  const clearSearchParams = useCallback(
    (keys: HbsSearchParamKey[]): void => {
      const params = new URLSearchParams(searchParams);
      keys.forEach(key => params.delete(key));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const navigateWithParams = useCallback(
    ({ to, samples }: NavigateIn): void => {
      navigate({
        pathname: to,
        search: createSearchParams({
          [HbsSearchParamKey.SAMPLE_CODES]: samples.map(sample => sample.sampleCode),
        }).toString(),
      });
    },
    [navigate],
  );

  const replaceSampleCodeWithId = useCallback(
    (sampleId: BioSampleId): void => {
      const params = new URLSearchParams(searchParams);

      if (params.has(HbsSearchParamKey.SAMPLE_CODE)) {
        params.delete(HbsSearchParamKey.SAMPLE_CODE);
        params.set(HbsSearchParamKey.SAMPLE_ID, sampleId);

        setSearchParams(params, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  return {
    visitId,
    patientId,
    sampleId,
    sampleCode,
    sampleCodes,
    kitCode,
    parentSampleId,
    clearSearchParams,
    setSearchParams,
    navigateWithParams,
    replaceSampleCodeWithId,
  };
};
